import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/lib/AuthContext";
import { logActivity } from "@/lib/activityLog";
import { TextInput, SelectInput, ErrorBanner } from "../shared/FormElements";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export default function ProjectDetail() {
  const { projectId } = useParams();
  const { profile } = useAuth();

  const [project, setProject] = useState(null);
  const [serviceLines, setServiceLines] = useState([]);
  const [savingServiceLine, setSavingServiceLine] = useState(false);
  const [metricDefs, setMetricDefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [postingNote, setPostingNote] = useState(false);

  const [entryDate, setEntryDate] = useState(todayISO());
  const [entryValues, setEntryValues] = useState({});
  const [saving, setSaving] = useState(false);

  const [history, setHistory] = useState([]);

  const [team, setTeam] = useState([]);
  const [newTeamName, setNewTeamName] = useState("");
  const [newTeamTitle, setNewTeamTitle] = useState("");

  useEffect(() => {
    loadProject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    if (project) loadEntryForDate(entryDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryDate, project]);

  async function loadProject() {
    setLoading(true);
    setError(null);

    const { data: proj, error: projError } = await supabase
      .from("projects")
      .select("*, clients(company_name), project_types(name)")
      .eq("id", projectId)
      .single();

    if (projError) {
      setError(projError.message);
      setLoading(false);
      return;
    }
    setProject(proj);

    const { data: linesData } = await supabase.from("service_lines").select("*").order("name");
    setServiceLines(linesData || []);

    const { data: allMetrics } = await supabase.from("master_metrics").select("*");
    const defs = (proj.metric_keys || []).map(
      (key) => (allMetrics || []).find((m) => m.key === key) || { key, display_name: key, unit: "count" }
    );
    setMetricDefs(defs);

    const { data: teamRows } = await supabase
      .from("project_team_members")
      .select("*")
      .eq("project_id", projectId);
    setTeam(teamRows || []);

    await loadHistory(defs);
    await loadNotes();
    setLoading(false);
  }

  async function saveServiceLine(serviceLineId) {
    setSavingServiceLine(true);
    await supabase.from("projects").update({ service_line_id: serviceLineId || null }).eq("id", projectId);
    setSavingServiceLine(false);
    loadProject();
  }

  async function loadNotes() {
    const { data } = await supabase
      .from("project_notes")
      .select("*, users(full_name, email)")
      .eq("project_id", projectId)
      .order("created_at", { ascending: false })
      .limit(20);
    setNotes(data || []);
  }

  async function postNote() {
    if (!newNote.trim()) return;
    setPostingNote(true);
    const { error: insertError } = await supabase.from("project_notes").insert({
      project_id: projectId,
      author_user_id: profile?.id,
      note: newNote.trim(),
    });
    setPostingNote(false);
    if (insertError) {
      setError(insertError.message);
      return;
    }
    setNewNote("");
    logActivity({
      actorId: profile?.id,
      clientId: project?.client_id,
      action: "Posted a note",
      details: project?.name,
    });
    loadNotes();
  }

  async function loadHistory(defs) {
    const { data } = await supabase
      .from("metrics")
      .select("*")
      .eq("project_id", projectId)
      .order("date", { ascending: false })
      .limit((defs?.length || 1) * 7);

    const byDate = {};
    (data || []).forEach((row) => {
      if (!byDate[row.date]) byDate[row.date] = { date: row.date };
      byDate[row.date][row.metric_key] = row.value;
    });
    const rows = Object.values(byDate).sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, 7);
    setHistory(rows);
  }

  async function loadEntryForDate(date) {
    const { data } = await supabase
      .from("metrics")
      .select("*")
      .eq("project_id", projectId)
      .eq("date", date);
    const values = {};
    (data || []).forEach((row) => {
      values[row.metric_key] = row.value;
    });
    setEntryValues(values);
  }

  function updateEntryValue(key, value) {
    setEntryValues((prev) => ({ ...prev, [key]: value }));
  }

  async function saveEntry() {
    setSaving(true);
    setError(null);
    const rows = metricDefs
      .filter((m) => entryValues[m.key] !== undefined && entryValues[m.key] !== "")
      .map((m) => ({
        project_id: projectId,
        date: entryDate,
        metric_key: m.key,
        value: parseFloat(entryValues[m.key]),
        source: "manual",
      }));

    if (rows.length === 0) {
      setSaving(false);
      setError("Enter at least one value before saving.");
      return;
    }

    const { error: upsertError } = await supabase
      .from("metrics")
      .upsert(rows, { onConflict: "project_id,date,metric_key" });

    setSaving(false);
    if (upsertError) {
      setError(upsertError.message);
      return;
    }
    setEntryValues({});
    logActivity({
      actorId: profile?.id,
      clientId: project.client_id,
      action: "Updated metrics",
      details: `${project.name}, ${entryDate}`,
    });
    loadHistory(metricDefs);
  }

  async function addTeamMember() {
    if (!newTeamName.trim() || !newTeamTitle.trim()) return;
    const { error: insertError } = await supabase.from("project_team_members").insert({
      project_id: projectId,
      member_name: newTeamName.trim(),
      title: newTeamTitle.trim(),
      visible_to_client: true,
    });
    if (insertError) {
      setError(insertError.message);
      return;
    }
    const addedName = newTeamName.trim();
    setNewTeamName("");
    setNewTeamTitle("");
    logActivity({
      actorId: profile?.id,
      clientId: project?.client_id,
      action: "Assigned team member",
      details: `${addedName}, ${project?.name}`,
    });
    loadProject();
  }

  async function removeTeamMember(id) {
    const removed = team.find((t) => t.id === id);
    await supabase.from("project_team_members").delete().eq("id", id);
    logActivity({
      actorId: profile?.id,
      clientId: project?.client_id,
      action: "Removed team member",
      details: `${removed?.member_name || "unknown"}, ${project?.name}`,
    });
    loadProject();
  }

  if (loading) return <p className="text-sm text-muted-foreground">Loading...</p>;
  if (!project) return <p className="text-sm text-destructive">Project not found.</p>;

  return (
    <div>
      <div className="text-xs text-muted-foreground mb-3">
        <Link to="/dashboard/agency/projects" className="hover:underline">
          Projects
        </Link>{" "}
        / {project.name}
      </div>

      <div className="flex items-center gap-2 mb-6">
        <h1 className="text-lg font-semibold text-foreground">{project.name}</h1>
        <span className="font-mono text-[10.5px] bg-accent text-foreground rounded-full px-2.5 py-1">
          {project.project_types?.name || "Custom"}
        </span>
        <select
          value={project.service_line_id || ""}
          onChange={(e) => saveServiceLine(e.target.value)}
          disabled={savingServiceLine}
          className="font-mono text-[10.5px] border border-input rounded-full px-2.5 py-1 bg-background text-muted-foreground ml-auto"
        >
          <option value="">No service line</option>
          {serviceLines.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      <ErrorBanner error={error} />

      <div className="grid grid-cols-[1.2fr_1fr] gap-5 items-start mb-5">
        <div className="border border-border rounded-2xl bg-card overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <span className="font-semibold text-sm text-foreground">Daily entry</span>
            <input
              type="date"
              value={entryDate}
              onChange={(e) => setEntryDate(e.target.value)}
              className="border border-input rounded-lg px-2.5 py-1.5 text-sm bg-background"
            />
          </div>
          <p className="text-[11px] text-muted-foreground px-5 pt-3">
            Pick an earlier date to backfill a missed day.
          </p>
          <div className="p-5">
            {metricDefs.map((m) => (
              <div key={m.key} className="flex items-center gap-3 py-2 border-b border-border last:border-0">
                <span className="text-sm flex-1">{m.display_name}</span>
                <input
                  className="w-32 border border-input rounded-lg px-2.5 py-1.5 text-sm text-right bg-background"
                  value={entryValues[m.key] ?? ""}
                  onChange={(e) => updateEntryValue(m.key, e.target.value)}
                />
                <span className="font-mono text-[10px] text-muted-foreground w-16">manual</span>
              </div>
            ))}
            <div className="flex justify-end mt-4">
              <button
                onClick={saveEntry}
                disabled={saving}
                className="bg-primary text-primary-foreground rounded-lg px-5 py-2 text-sm font-semibold disabled:opacity-60"
              >
                {saving ? "Saving..." : "Save entry"}
              </button>
            </div>
          </div>
        </div>

        <div className="border border-border rounded-2xl bg-card overflow-hidden">
          <div className="px-5 py-4 border-b border-border font-semibold text-sm text-foreground">
            Last 7 days
          </div>
          {history.length === 0 ? (
            <p className="text-sm text-muted-foreground p-5">No entries yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left font-mono text-[10px] uppercase text-muted-foreground px-5 py-2.5">Date</th>
                  {metricDefs.map((m) => (
                    <th key={m.key} className="text-right font-mono text-[10px] uppercase text-muted-foreground px-5 py-2.5">
                      {m.display_name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.map((row) => (
                  <tr key={row.date} className="border-b border-border last:border-0">
                    <td className="px-5 py-2.5">{row.date}</td>
                    {metricDefs.map((m) => (
                      <td key={m.key} className="px-5 py-2.5 text-right font-mono text-xs">
                        {row[m.key] ?? "-"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="border border-border rounded-2xl bg-card overflow-hidden">
        <div className="px-5 py-4 border-b border-border font-semibold text-sm text-foreground">Team</div>
        <div>
          {team.map((t) => (
            <div key={t.id} className="flex items-center gap-3 px-5 py-3 border-b border-border last:border-0">
              <div className="w-8 h-8 rounded-full bg-accent text-foreground flex items-center justify-center text-xs font-semibold">
                {(t.member_name || "?").slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1">
                <div className="text-sm font-medium">{t.member_name}</div>
                <div className="text-xs text-muted-foreground">
                  {t.title}, {t.visible_to_client ? "visible to client" : "hidden from client"}
                </div>
              </div>
              <button onClick={() => removeTeamMember(t.id)} className="text-xs text-muted-foreground">
                Remove
              </button>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 px-5 py-4">
          <TextInput
            placeholder="Name, e.g. Priya Shah"
            value={newTeamName}
            onChange={(e) => setNewTeamName(e.target.value)}
          />
          <TextInput
            placeholder="Title, e.g. Project manager"
            value={newTeamTitle}
            onChange={(e) => setNewTeamTitle(e.target.value)}
          />
          <button onClick={addTeamMember} className="border border-input rounded-lg px-4 py-2 text-sm whitespace-nowrap">
            + Add
          </button>
        </div>
      </div>

      <div className="border border-border rounded-2xl bg-card overflow-hidden mt-5">
        <div className="px-5 py-4 border-b border-border font-semibold text-sm text-foreground">
          Notes, visible to the client
        </div>
        <div className="p-5">
          <textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Add an update the client will see"
            className="w-full border border-input rounded-lg px-3 py-2 text-sm bg-background text-foreground min-h-[60px]"
          />
          <div className="flex justify-end mt-2">
            <button
              onClick={postNote}
              disabled={postingNote}
              className="border border-input rounded-lg px-4 py-2 text-sm disabled:opacity-60"
            >
              {postingNote ? "Posting..." : "Post note"}
            </button>
          </div>
          <div className="mt-4">
            {notes.length === 0 ? (
              <p className="text-sm text-muted-foreground">No notes yet.</p>
            ) : (
              notes.map((n) => (
                <div key={n.id} className="py-2.5 border-b border-border last:border-0 text-sm">
                  <div className="font-mono text-[11px] text-muted-foreground mb-0.5">
                    {new Date(n.created_at).toLocaleDateString()} &middot; {n.users?.full_name || n.users?.email}
                  </div>
                  {n.note}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
